import { getPortals, PM2Portal } from "@courses-pm2/api";

/**
 * Internal cache of portals available within the PM2 backend, so that we can
 * safely resolve links to them.
 */
let cache: PM2Portal[] | undefined;

/**
 * Helper that'll open an internal link on a pm2 portal in a new window (first
 * resolving the base url of the portal).
 */
export async function openInternalLink({ path, portalId }: {
    path: string;
    portalId: string;
}): Promise<void> {
    // Open a new window instantly, so it will occur directly within the user's
    // input event
    const win = window.open("about:blank", "_blank");

    // Bail out if we were unable to open the new window
    if (!win) {
        alert("Vi kunne ikke åbne linket, vi beklager.");

        // Abort further execution
        return;
    }

    try {
        // If portals haven't been fetched, then do so now
        if (!cache) {
            cache = await getPortals();
        }

        const portal = cache.find((tmpPortal) => tmpPortal.id === portalId);

        // Bail out if unable to find the portal
        if (!portal || !portal.primaryDomainName) {
            throw new Error("openInternalLink(...): Unable to find portal primary domain.");
        }

        // Determine if we're supposed to use http or https when accessing the
        // portal based on the primary domain
        const domain = (
            portal.primaryDomainName.indexOf("//") !== -1
                ? portal.primaryDomainName
                    .split("//")[1]
                    .split("/")[0]
                : portal.primaryDomainName
                    .split("/")[0]
        );

        const protocol = (
            domain.match(/^[^\.]+\.(gyldendal|munksgaard|hansreitzel)\.dk$/i)
                ? "https://"
                : "http://" // tslint:disable-line no-http-string
        );

        // Generate complete url
        const url = `${protocol}${domain}/${path.replace(/^\//, "")}`;

        // Open the url!
        win.location.href = url;
    } catch (err) {
        // Propagate the error to the console
        console.error(err); // tslint:disable-line no-console

        // Notify the user of the issue with opening the page
        win.alert("Vi kunne ikke åbne linket, vi beklager.");
        win.close();
    }
}
